import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { AppLayout } from './components/AppLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import AccountPage from './pages/AccountPage';
import ActivatePage from './pages/ActivatePage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import ClassDetailPage from './pages/ClassDetailPage';
import ClassesPage from './pages/ClassesPage';
import HomePage from './pages/HomePage';
import JoinPage from './pages/JoinPage';
import LoginPage from './pages/LoginPage';
import MyFlowsPage from './pages/MyFlowsPage';
import NewClassPage from './pages/NewClassPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import RegisterPage from './pages/RegisterPage';

/**
 * O bpmn-js sozinho pesa cerca de 600 kB. Carregado junto com o resto, ele
 * seria baixado por todo mundo — inclusive por quem so entra para ver a turma.
 * Com `lazy`, ele so viaja pela rede quando alguem realmente abre um editor.
 */
const BpmnPage = lazy(() => import('./pages/BpmnPage'));
const MyFlowEditorPage = lazy(() => import('./pages/MyFlowEditorPage'));
const VersionViewPage = lazy(() => import('./pages/VersionViewPage'));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage'));
const ProblemsPage = lazy(() => import('./pages/ProblemsPage'));
const IshikawaPage = lazy(() => import('./pages/IshikawaPage'));
const OkrPage = lazy(() => import('./pages/OkrPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BackupPage = lazy(() => import('./pages/BackupPage'));
const AccessSettingsPage = lazy(() => import('./pages/AccessSettingsPage'));
const ImplementationPlanPage = lazy(() => import('./pages/ImplementationPlanPage'));
const GanttPage = lazy(() => import('./pages/GanttPage'));
const PdcaPage = lazy(() => import('./pages/PdcaPage'));
const FutureVisionPage = lazy(() => import('./pages/FutureVisionPage'));
const SipocPage = lazy(() => import('./pages/SipocPage'));

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />

          {/* Chegada do login social (PRD 33.5) — o backend ja criou a
              sessao antes de redirecionar pra ca. */}
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />

          {/* Convite por QR Code. Exige sessao, mas fica fora do layout: o
              aluno cai aqui vindo do celular, sem contexto de navegacao. */}
          <Route path="/entrar/:kind/:token" element={<JoinPage />} />

          {/* Ativacao de conta pre-cadastrada (PRD 33.7). */}
          <Route path="/ativar/:token" element={<ActivatePage />} />

          {/* Tudo abaixo exige sessao. A guarda real fica no backend. */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<HomePage />} />

              <Route path="/turmas" element={<ClassesPage />} />
              <Route path="/turmas/nova" element={<NewClassPage />} />
              <Route path="/turmas/:classId" element={<ClassDetailPage />} />
              <Route
                path="/turmas/:classId/atividades/:activityId"
                element={<ActivityDetailPage />}
              />

              {/* Editor do projeto academico. O id da instancia identifica de
                  quem e o projeto — o backend confere o acesso. */}
              <Route
                path="/projetos/:instanceId/bpmn"
                element={
                  <Suspense fallback={<p className="muted">Carregando o editor…</p>}>
                    <BpmnPage />
                  </Suspense>
                }
              />

              {/* FASE 9 — Analise do Processo. */}
              <Route
                path="/projetos/:instanceId/analise"
                element={
                  <Suspense fallback={<p className="muted">Carregando a análise…</p>}>
                    <AnalysisPage />
                  </Suspense>
                }
              />

              {/* FASE 10 — Problemas. */}
              <Route
                path="/projetos/:instanceId/problemas"
                element={
                  <Suspense fallback={<p className="muted">Carregando os problemas…</p>}>
                    <ProblemsPage />
                  </Suspense>
                }
              />

              {/* FASE 11 — Ishikawa 4M, aberto a partir de um problema. */}
              <Route
                path="/projetos/:instanceId/problemas/:problemId/ishikawa"
                element={
                  <Suspense fallback={<p className="muted">Carregando o Ishikawa…</p>}>
                    <IshikawaPage />
                  </Suspense>
                }
              />

              {/* FASE 12 — OKR. */}
              <Route
                path="/projetos/:instanceId/okr"
                element={
                  <Suspense fallback={<p className="muted">Carregando o OKR…</p>}>
                    <OkrPage />
                  </Suspense>
                }
              />

              {/* FASE 19 — Plano de Implantacao / 5W1H. */}
              <Route
                path="/projetos/:instanceId/plano-implantacao"
                element={
                  <Suspense fallback={<p className="muted">Carregando o plano…</p>}>
                    <ImplementationPlanPage />
                  </Suspense>
                }
              />

              {/* FASE 20 — Cronograma fisico / Gantt. */}
              <Route
                path="/projetos/:instanceId/gantt"
                element={
                  <Suspense fallback={<p className="muted">Carregando o cronograma…</p>}>
                    <GanttPage />
                  </Suspense>
                }
              />

              {/* FASE 21 — PDCA. */}
              <Route
                path="/projetos/:instanceId/pdca"
                element={
                  <Suspense fallback={<p className="muted">Carregando o PDCA…</p>}>
                    <PdcaPage />
                  </Suspense>
                }
              />

              {/* FASE 22 — Visão Futura, primeira etapa da metodologia (antes
                  do AS-IS). */}
              <Route
                path="/projetos/:instanceId/visao-futura"
                element={
                  <Suspense fallback={<p className="muted">Carregando a visão futura…</p>}>
                    <FutureVisionPage />
                  </Suspense>
                }
              />

              {/* FASE 23 — SIPOC, junto com a Visão Futura o passo 1 da
                  metodologia (PRD seção 32). */}
              <Route
                path="/projetos/:instanceId/sipoc"
                element={
                  <Suspense fallback={<p className="muted">Carregando o SIPOC…</p>}>
                    <SipocPage />
                  </Suspense>
                }
              />

              {/* FASE 16 — dashboard do professor. */}
              <Route
                path="/turmas/:classId/dashboard"
                element={
                  <Suspense fallback={<p className="muted">Carregando o dashboard…</p>}>
                    <DashboardPage />
                  </Suspense>
                }
              />

              {/* FASE 6 — visualizacao de versao congelada (somente leitura). */}
              <Route
                path="/versoes/:versionId"
                element={
                  <Suspense fallback={<p className="muted">Carregando a versão…</p>}>
                    <VersionViewPage />
                  </Suspense>
                }
              />

              {/* FASE 18 — backup. Configuracao de plataforma, fora de /turmas.
                  A guarda de verdade e o backend (so professor); o frontend
                  so esconde o link no menu para quem nao e professor. */}
              <Route
                path="/backup"
                element={
                  <Suspense fallback={<p className="muted">Carregando o backup…</p>}>
                    <BackupPage />
                  </Suspense>
                }
              />

              {/* FASE 24 — acesso e identidade. Configuracao de plataforma,
                  fora de /turmas, mesmo padrao do /backup. */}
              <Route
                path="/acesso"
                element={
                  <Suspense fallback={<p className="muted">Carregando o acesso…</p>}>
                    <AccessSettingsPage />
                  </Suspense>
                }
              />

              {/* FASE 5b — area pessoal, fora das atividades. */}
              <Route path="/meus-fluxos" element={<MyFlowsPage />} />
              <Route
                path="/meus-fluxos/:flowId"
                element={
                  <Suspense fallback={<p className="muted">Carregando o editor…</p>}>
                    <MyFlowEditorPage />
                  </Suspense>
                }
              />

              {/* FASE 25 — troca da própria senha (PRD 34.2), professor e
                  aluno. */}
              <Route path="/conta" element={<AccountPage />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
